<template>
    <div class="o-cp-header">
        <div class="o-cp-header__left">
            <div
                class="o-cp-header__logo"
                :class="imageClass"
                @click="openImageEditModal"
            >
                <cp-logo-rectangle
                    v-if="firmLogo"
                    :logo="firmLogo"
                    @loadFailed="logoLoadFailed('firm')"
                >
                </cp-logo-rectangle>
                <div
                    v-else
                    class="u-primary-color__text u-ellipsis"
                >
                    {{ nal.firm.name }}
                </div>
                <icon-button
                    v-if="projectId && isAuthEdit"
                    class="o-cp-header__edit-image"
                    :tooltip="trans('common.edit')"
                    icon="fa-pencil-alt"
                    iconStyle="light"
                    size="2xs"
                    color="white"
                    hasShadow
                ></icon-button>
            </div>
        </div>
        <div class="o-cp-header__right">
            <template v-if="isLoggedIn">
                <i
                    v-if="isSearch && isDisabled"
                    v-tooltip="trans('client-portal.settings.disabled-project')"
                    class="fa-regular fa-shield-exclamation o-cp-header__disabled u-hover"
                    @click="showSettingsModal"
                ></i>
                <icon-button
                    v-if="isCompactActionButtons && isAuthEdit && _portal"
                    :tooltip="trans('client-portal.settings.restrict')"
                    icon="fa-user-shield"
                    size="sm"
                    @click="showRestrictSettingsModal"
                >
                </icon-button>
                <base-button
                    v-else-if="isAuthEdit && _portal"
                    size="sm"
                    :text="trans('client-portal.settings.restrict')"
                    icon="fa-user-shield"
                    @click="showRestrictSettingsModal"
                >
                </base-button>
                <cp-invite
                    v-if="isSearch && _project"
                    :project="_project"
                    :iconOnly="isCompactActionButtons"
                ></cp-invite>
                <icon-button
                    v-if="isCompactActionButtons && isAuthEdit && _portal"
                    :tooltip="trans('settings.client-portal.templates.title')"
                    icon="fa-diagram-lean-canvas"
                    size="sm"
                    @click="openTemplateModal"
                >
                </icon-button>
                <base-button
                    v-else-if="isAuthEdit && _portal"
                    size="sm"
                    :text="trans('settings.client-portal.templates.title')"
                    icon="fa-diagram-lean-canvas"
                    @click="openTemplateModal"
                >
                </base-button>
                <div class="o-cp-header__primary-actions">
                    <cp-preview
                        v-if="isTrueSearch && hasProjects && _project"
                        :isSaving="isSaving"
                        :client="_project.client"
                    >
                    </cp-preview>
                    <cp-menu></cp-menu>
                </div>
            </template>
            <div v-else>
                <router-link
                    :to="{ name: 'login' }"
                    class="u-hover-text"
                >
                    {{ trans('auth.buttons.login') }}
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import CpMenu from '@js/client-portal/vue-components/layout/menu/cp-menu.vue';
import CpPreview from '@js/client-portal/vue-components/layout/buttons/cp-preview.vue';
import CpInvite from '@js/client-portal/vue-components/layout/buttons/cp-invite.vue';
import CpLogoRectangle from '@js/client-portal/vue-components/general/cp-logo-rectangle.vue';

import interactsWithProject from '@js/client-portal/vue-mixins/interactsWithProject.js';
import interactsWithLogoLoad from '@js/client-portal/vue-mixins/interactsWithLogoLoad.js';
import { defineAsyncComponent } from 'vue';
import { useFirmVisibilityStore } from '@js/stores/firmVisibilityStore.js';

export default {
    name: 'CpHeader',
    components: {
        CpMenu,
        CpPreview,
        CpInvite,
        CpLogoRectangle,
    },
    mixins: [
        interactsWithProject,
        interactsWithLogoLoad,
    ],
    props: {
        isSaving: Boolean,
    },
    data() {
        return {
            isCompactActionButtons: false,
        };
    },
    computed: {
        firmVisibilityStore() {
            return useFirmVisibilityStore();
        },
        firmLogo() {
            if (!this.isLoggedIn) {
                return this.nal.logo || '';
            }
            if (this.hasProjects && !this.projectId) {
                const firstFirmProject = this.projects.find(project => project.search_firm_id === this.nal.firm.id);
                return firstFirmProject?.portal.firmLogoUrl || this.originalFirmLogo;
            }
            return this.portalFirmLogo || this.originalFirmLogo;
        },
        originalFirmLogo() {
            return this.nal.firm.logo_url || '';
        },
        portalFirmLogo() {
            return this.projectId && this._project.portal.firmLogoUrl || '';
        },
        isDisabled() {
            return this.projectId && this._project.portal.disabled;
        },
        isAuthEdit() {
            return this.isSearch
                && this.projectId
                && this._project
                && window.auth('edit', this._project.label.plural(), this._project);
        },
        isLoggedIn() {
            return !!this.nal.user?.role;
        },
        imageClass() {
            return this.isAuthEdit
                ? 'u-hover u-primary-color__border--hover'
                : 'u-no-pointer';
        },
    },
    methods: {
        updateFirmLogo({ firmLogoUrl, firmLogoColor }) {
            this.updateProjectPortal(this.projectId, firmLogoUrl, 'firmLogoUrl');
            this.updateProjectPortal(this.projectId, firmLogoColor, 'firmLogoColor');
        },
        openImageEditModal() {
            if (this.isAuthEdit) {
                this.createModal({
                    data: {
                        component: defineAsyncComponent(() =>
                            import('@js/client-portal/vue-components/general/images/cp-image-edit-modal.vue')),
                        style: { height: 'fit-content' },
                        data: {
                            defaultImage: this.originalFirmLogo,
                            image: this.portalFirmLogo,
                            formKey: 'firmLogoUrl',
                            formUrl: `portal/${this._project.portal.id}`,
                            title: trans('client-portal.common.edit-image'),
                        },
                        on: {
                            logoUpdate: (data) => this.updateFirmLogo(data),
                        },
                    },
                });
            }
        },
        showSettingsModal() {
            this.createModal({
                data: {
                    component: defineAsyncComponent(() =>
                        import('@js/client-portal/vue-components/settings/cp-settings.vue')),
                    style: { width: '750px' },
                },
            });
        },
        showRestrictSettingsModal() {
            this.createModal({
                data: {
                    component: defineAsyncComponent(() =>
                        import('@js/client-portal/vue-components/settings/cp-settings.vue')),
                    style: { width: '750px' },
                    data: {
                        selectedTab: 'CpSettingsRestrict',
                    },
                },
            });
        },
        openTemplateModal() {
            this.createModal({
                data: {
                    component: defineAsyncComponent(() =>
                        import('@js/nal/vue-components/models/projects/assets/create-portal-modal.vue')),
                    style: { width: '800px', height: '800px' },
                    data: {
                        isChangeTemplate: true,
                        projectId: this.projectId,
                        portalId: this._portal.id,
                    },
                    on: {
                        templateChanged: (portal) => {
                            this.updateProjectPortal(this.projectId, portal, 'portal');
                            window.location.reload();
                        },
                    },
                },
            });
        },
        updateActionButtonsMode() {
            this.isCompactActionButtons = window.innerWidth <= 720;
        },
        loadVisibilitySettings() {
            try {
                this.firmVisibilityStore.setLoading(true);
                this.get(`/portal/${this._portal.id}/visibility`).then(response => {
                    this.firmVisibilityStore.setVisibilitySettings(response);
                });
            } finally {
                this.firmVisibilityStore.setLoading(false);
            }
        },
        resizeListener: _.throttle(function () {
            this.updateActionButtonsMode();
        }, 200),
    },
    watch: {
        _portal(val) {
            if (val) {
                this.loadVisibilitySettings();
            }
        },
    },
    mounted() {
        this.updateActionButtonsMode();
        window.addEventListener('resize', this.resizeListener);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.resizeListener);
    },
};
</script>

<style lang="scss" scoped>

@use "@sass/variables";

.o-cp-header {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px 14px;
    max-width: 1500px;
    padding: 0 20px;
    width: 100%;

    &__right, &__left {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 10px 14px;
        position: relative;
    }

    &__left {
        flex: 0 1 auto;
        min-width: 0;
    }

    &__right {
        gap: 8px 10px;
        justify-content: flex-end;
        margin-left: auto;
    }

    &__logo {
        align-items: center;
        border-radius: 10px;
        display: flex;
        height: 40px;
        max-width: 180px;
        overflow: hidden;
    }

    &__primary-actions {
        align-items: center;
        display: inline-flex;
        flex: 0 0 auto;
        gap: 8px;
        white-space: nowrap;
    }

    &__disabled {
        color: variables.$color__amber;
        font-size: 26px;
        padding: 6px;
    }

    &__edit-image {
        position: absolute !important;
        right: -5px;
        top: 25px;
    }
}

@media (width <= 1200px) {
    .o-cp-header {
        padding: 0 20px;
    }
}

@media (width <= 700px) {
    .o-cp-header {
        padding: 0 10px;

        &__logo {
            max-width: 90px;
        }
    }
}
</style>
